import { z } from "zod";
export const quickBooksConnectionStatusSchema = z.enum([
    "NOT_CONNECTED",
    "CONNECTED",
    "NEEDS_ATTENTION",
]);
export const quickBooksBootstrapSchema = z.object({
    providerConfigured: z.boolean(),
    connection: z.object({
        status: quickBooksConnectionStatusSchema,
        companyName: z.string().nullable(),
        realmId: z.string().nullable(),
        connectedAt: z.string().datetime().nullable(),
        lastError: z.string().nullable(),
    }),
    lastSync: z
        .object({
        status: z.enum(["SUCCEEDED", "FAILED"]),
        customersSynced: z.number().int().min(0),
        invoicesSynced: z.number().int().min(0),
        completedAt: z.string().datetime(),
        error: z.string().nullable(),
    })
        .nullable(),
});
export const quickBooksOAuthStartResponseSchema = z.object({
    authorizationUrl: z.string().url(),
});
export const quickBooksSyncResponseSchema = z.object({
    status: z.literal("SUCCEEDED"),
    customersSynced: z.number().int().min(0),
    invoicesSynced: z.number().int().min(0),
    completedAt: z.string().datetime(),
});
export const QUICKBOOKS_SYNC_V2_CONTRACT_VERSION = 2;
export const quickBooksSyncEntityTypeSchema = z.enum([
    "CUSTOMER",
    "ITEM",
    "TAX_RATE",
    "INVOICE",
    "PAYMENT",
    "REFUND",
]);
export const quickBooksRecordStatusSchema = z.enum([
    "PENDING",
    "IN_SYNC",
    "FAILED",
    "CONFLICT",
    "DEAD_LETTER",
]);
export const quickBooksSyncJobStatusSchema = z.enum([
    "QUEUED",
    "RUNNING",
    "RETRYING",
    "SUCCEEDED",
    "PARTIAL",
    "FAILED",
    "DEAD_LETTER",
]);
export const quickBooksSyncTriggerSchema = z.enum([
    "MANUAL",
    "CREWFSM_EVENT",
    "RECONCILIATION",
    "REDRIVE",
]);
export const quickBooksSafeErrorCodeSchema = z.enum([
    "AUTHORIZATION_EXPIRED",
    "RATE_LIMITED",
    "PROVIDER_UNAVAILABLE",
    "PROVIDER_VALIDATION_FAILED",
    "PROVIDER_CONFLICT",
    "MAPPING_DEPENDENCY_MISSING",
    "CREWFSM_EXPORT_UNAVAILABLE",
    "UNEXPECTED_RESPONSE",
]);
export const quickBooksSyncRecordSchema = z.object({
    entityType: quickBooksSyncEntityTypeSchema,
    crewFsmId: z.string().min(1).max(200),
    quickBooksId: z.string().min(1).max(200).nullable(),
    providerSyncToken: z.string().max(120).nullable(),
    sourceFingerprint: z
        .string()
        .regex(/^[a-f0-9]{64}$/)
        .nullable(),
    status: quickBooksRecordStatusSchema,
    attemptCount: z.number().int().nonnegative(),
    lastAttemptAt: z.string().datetime().nullable(),
    lastSuccessfulSyncAt: z.string().datetime().nullable(),
    errorCode: quickBooksSafeErrorCodeSchema.nullable(),
    errorMessage: z.string().max(1_000).nullable(),
    retryable: z.boolean(),
});
export const quickBooksSyncJobSchema = z.object({
    id: z.string().uuid(),
    trigger: quickBooksSyncTriggerSchema,
    status: quickBooksSyncJobStatusSchema,
    attemptCount: z.number().int().nonnegative(),
    recordsTotal: z.number().int().nonnegative(),
    recordsSucceeded: z.number().int().nonnegative(),
    recordsFailed: z.number().int().nonnegative(),
    nextAttemptAt: z.string().datetime().nullable(),
    startedAt: z.string().datetime().nullable(),
    completedAt: z.string().datetime().nullable(),
    lastErrorCode: quickBooksSafeErrorCodeSchema.nullable(),
    lastErrorMessage: z.string().max(1_000).nullable(),
    createdAt: z.string().datetime(),
});
export const quickBooksV2HealthSchema = z.object({
    contractVersion: z.literal(QUICKBOOKS_SYNC_V2_CONTRACT_VERSION),
    sourceOfTruth: z.literal("CREWFSM_OUTBOUND"),
    connection: z.object({
        status: quickBooksConnectionStatusSchema,
        companyName: z.string().nullable(),
        realmId: z.string().nullable(),
        connectedAt: z.string().datetime().nullable(),
        lastError: z.string().nullable(),
        accessTokenExpiresAt: z.string().datetime().nullable(),
        refreshTokenExpiresAt: z.string().datetime().nullable(),
        rateLimitedUntil: z.string().datetime().nullable(),
    }),
    lastSuccessfulSyncAt: z.string().datetime().nullable(),
    counts: z.object({
        pending: z.number().int().nonnegative(),
        inSync: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        conflicts: z.number().int().nonnegative(),
        deadLetter: z.number().int().nonnegative(),
    }),
    latestJob: quickBooksSyncJobSchema.nullable(),
});
export const quickBooksQueueSyncRequestSchema = z.object({
    idempotencyKey: z.string().uuid(),
    reconcile: z.boolean().default(false),
});
export const quickBooksQueueSyncResponseSchema = z.object({
    job: quickBooksSyncJobSchema,
});
export const quickBooksSyncRecordsQuerySchema = z.object({
    entityType: quickBooksSyncEntityTypeSchema.optional(),
    status: quickBooksRecordStatusSchema.optional(),
    limit: z.coerce.number().int().min(1).max(250).default(100),
});
export const quickBooksSyncRecordsResponseSchema = z.object({
    items: z.array(quickBooksSyncRecordSchema),
});
export const quickBooksRedriveResponseSchema = z.object({
    job: quickBooksSyncJobSchema,
});
